"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ChevronLeft, ArrowLeft } from "lucide-react";
import { useT } from "@/components/common/locale-context";

// Root not-found: Next renders this for any unmatched URL across the whole app
// (and for notFound() calls without a closer boundary). It lives inside the root
// layout only — no topbar/sidebar — so it centres in the full viewport. Visual:
// a glass card over a field of little arrows that all point different ways (you're
// lost — no clear direction) and swivel toward the cursor like a compass field as
// you move the mouse. The "0" in 404 is a googly eye searching for the page, and a
// small working terminal lets you `home` / `back` your way out.

type Line = { kind: "cmd" | "dim" | "err" | "ok"; text: string };

const COLOR: Record<Line["kind"], string> = {
  cmd: "var(--ink)",
  dim: "var(--ink-3)",
  err: "var(--s-stuck)",
  ok: "var(--m-docs)",
};

const CELL = 76; // px — spacing of the arrow grid
const GOLDEN = 137.508; // deg — scatters the resting rotations so they read as "lost"

export default function NotFound() {
  const t = useT();
  const pathname = usePathname();
  const router = useRouter();

  // Intro transcript: a fake `find` that scans the app's modules and comes up
  // empty, then hands you a live prompt.
  const intro: Line[] = [
    { kind: "cmd", text: `find /moduly${pathname ?? ""}` },
    { kind: "dim", text: t.errors.notFoundTermScan },
    { kind: "err", text: `✗ ${t.errors.notFoundTermErr} (404)` },
    { kind: "dim", text: t.errors.notFoundTermHint },
  ];

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const [visible, setVisible] = useState(reduce ? intro.length : 0);
  const ready = visible >= intro.length;
  const [log, setLog] = useState<Line[]>([]);
  const [cmd, setCmd] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  // Arrow field state: grid dimensions (recomputed on resize) + refs to each
  // arrow so the pointer handler can retarget them without re-rendering React.
  const [grid, setGrid] = useState({ cols: 0, rows: 0 });
  const gridRef = useRef({ cols: 0, rows: 0 });
  const arrowsRef = useRef<HTMLSpanElement[]>([]);

  // Reveal the intro lines one at a time (skipped under reduced-motion).
  useEffect(() => {
    if (reduce || ready) return;
    const id = setTimeout(() => setVisible((v) => v + 1), visible === 0 ? 250 : 500);
    return () => clearTimeout(id);
  }, [visible, ready, reduce]);

  // Keep the transcript pinned to the bottom as it grows.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log, visible]);

  // Size the arrow grid to the viewport (and re-size on resize).
  useEffect(() => {
    const measure = () => {
      const cols = Math.max(1, Math.ceil(window.innerWidth / CELL));
      const rows = Math.max(1, Math.ceil(window.innerHeight / CELL));
      gridRef.current = { cols, rows };
      setGrid({ cols, rows });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Cursor-reactive compass field: every arrow rotates to face the pointer and
  // the ones near it brighten & grow — a pool of light sweeping the lost field.
  // rAF-throttled, transforms written straight to the DOM (no React churn).
  useEffect(() => {
    if (reduce) return;
    let x = -1;
    let y = -1;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const { cols, rows } = gridRef.current;
      if (!cols) return;
      const cw = window.innerWidth / cols;
      const ch = window.innerHeight / rows;
      const n = cols * rows;
      for (let i = 0; i < n; i++) {
        const el = arrowsRef.current[i];
        if (!el) continue;
        const cx = ((i % cols) + 0.5) * cw;
        const cy = (Math.floor(i / cols) + 0.5) * ch;
        const dx = x - cx;
        const dy = y - cy;
        const ang = (Math.atan2(dy, dx) * 180) / Math.PI + 90; // +90: arrows rest pointing up
        const near = Math.max(0, 1 - Math.hypot(dx, dy) / 340); // 0..1 within ~340px
        el.style.transform = `rotate(${ang}deg) scale(${1 + near * 1.1})`;
        el.style.opacity = String(0.16 + near * 0.64);
      }
      bgRef.current?.style.setProperty("--nf-mx", `${x}px`);
      bgRef.current?.style.setProperty("--nf-my", `${y}px`);
    };
    const onMove = (e: MouseEvent) => {
      x = e.clientX;
      y = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduce]);

  function run(raw: string) {
    const c = raw.trim().toLowerCase();
    setCmd("");
    if (!c) return;
    const echo: Line = { kind: "cmd", text: c };

    if (c === "clear" || c === "cls") return setLog([]);
    if (["help", "?"].includes(c))
      return setLog((l) => [...l, echo, { kind: "dim", text: t.errors.notFoundTermHelp }]);
    if (["home", "dashboard", "domov", "cd ~", "cd", "~"].includes(c)) {
      setLog((l) => [...l, echo, { kind: "ok", text: "→ dashboard" }]);
      router.push("/");
      return;
    }
    if (["back", "spat", "späť", "cd .."].includes(c)) {
      setLog((l) => [...l, echo, { kind: "ok", text: "→ …" }]);
      window.history.back();
      return;
    }
    setLog((l) => [...l, echo, { kind: "err", text: `${t.errors.notFoundTermUnknown}: ${c}` }]);
  }

  const transcript = [...intro.slice(0, visible), ...log];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      {/* Backdrop — a "lost" compass field: arrows scattered every which way that
          swivel toward the cursor, plus a soft light pooling under the pointer. */}
      <div className="nf-bg" ref={bgRef} aria-hidden>
        <div
          className="nf-arrows"
          style={{ gridTemplateColumns: `repeat(${grid.cols}, 1fr)` }}
        >
          {Array.from({ length: grid.cols * grid.rows }).map((_, i) => (
            <span
              key={i}
              ref={(el) => {
                if (el) arrowsRef.current[i] = el;
              }}
              className="nf-arrow"
              style={{ transform: `rotate(${(i * GOLDEN) % 360}deg)` }}
            />
          ))}
        </div>
        <div className="nf-spot" />
      </div>

      <div className="glass rise relative z-10 w-[480px] max-w-full rounded-[16px] p-8 text-center">
        <div
          data-testid="not-found-404"
          role="img"
          aria-label="404"
          className="nf-404 justify-center text-[84px] font-black leading-none tracking-tight tabular-nums"
        >
          <span aria-hidden>4</span>
          <span className="nf-eye" aria-hidden>
            <span className="nf-pupil" />
            <span className="nf-lid" />
          </span>
          <span aria-hidden>4</span>
        </div>

        <h1 className="mt-6 text-[clamp(22px,3.4vw,29px)] font-semibold leading-[1.15] tracking-tight text-balance text-[var(--ink)]">
          {t.errors.pageNotFound}{" "}
          <span className="font-semibold" style={{ color: "var(--m-docs)" }}>
            {t.errors.pageNotFoundEmphasis}
          </span>
          .
        </h1>
        <p className="mx-auto mt-3 max-w-[400px] text-[14px] leading-[1.65] text-pretty text-[var(--ink-2)]">
          {t.errors.pageNotFoundSub}
        </p>
        <p className="mx-auto mt-1.5 text-[14px] font-medium text-[var(--ink)]">
          {t.errors.pageNotFoundGood}
        </p>

        {/* Working terminal — type `home`, `back`, `help`, `clear`. */}
        <div
          className="glass-soft mt-6 overflow-hidden rounded-[12px] text-left"
          onClick={() => inputRef.current?.focus()}
        >
          <div
            className="flex items-center gap-1.5 border-b px-3.5 py-2.5"
            style={{ borderColor: "var(--line)" }}
          >
            <span className="size-2.5 rounded-full" style={{ background: "var(--s-stuck)" }} />
            <span className="size-2.5 rounded-full" style={{ background: "var(--s-potential)" }} />
            <span className="size-2.5 rounded-full" style={{ background: "var(--s-done)" }} />
            <span
              className="ml-2 text-[11px] text-[var(--ink-3)]"
              style={{ fontFamily: "var(--font-mono)" }}
            >
              macay@core — vyhľadávanie
            </span>
          </div>

          <div
            ref={bodyRef}
            className="max-h-[168px] overflow-y-auto px-3.5 py-3 text-[12.5px] leading-relaxed"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {transcript.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap" style={{ color: COLOR[l.kind] }}>
                {l.kind === "cmd" && <span style={{ color: "var(--m-docs)" }}>macay ~ </span>}
                {l.text}
              </div>
            ))}

            {ready ? (
              <div className="flex items-center whitespace-pre-wrap" style={{ color: "var(--ink)" }}>
                <span style={{ color: "var(--m-docs)" }}>macay ~ </span>
                <input
                  ref={inputRef}
                  value={cmd}
                  onChange={(e) => setCmd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && run(cmd)}
                  aria-label="terminál"
                  spellCheck={false}
                  autoComplete="off"
                  autoCapitalize="off"
                  className="flex-1 border-0 bg-transparent p-0 outline-none focus:ring-0"
                  style={{ fontFamily: "var(--font-mono)", color: "var(--ink)", caretColor: "var(--m-docs)" }}
                />
              </div>
            ) : (
              <div style={{ color: "var(--m-docs)" }}>
                macay ~{" "}
                <span
                  className={`inline-block h-3.5 w-2 translate-y-0.5 ${reduce ? "" : "animate-pulse"}`}
                  style={{ background: "var(--m-docs)" }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 rounded-[10px] bg-[var(--btn-bg)] px-4 py-2 text-[13px] font-semibold text-[var(--btn-fg)] transition-colors hover:bg-[var(--btn-bg-hover)]"
          >
            <ChevronLeft className="size-4" /> {t.errors.goHome}
          </Link>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1.5 rounded-[10px] border px-4 py-2 text-[13px] font-semibold text-[var(--ink)] transition-colors hover:bg-[var(--w-hover)]"
            style={{ borderColor: "var(--line)" }}
          >
            <ArrowLeft className="size-4" /> {t.errors.goBack}
          </button>
        </div>
      </div>
    </div>
  );
}
